import {Box, createStyles, Text} from '@mantine/core';
import React, {useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  LinkerPayloadLeavingGroupsQuery,
  LinkerPayloadLeavingGroupsQueryVariables,
} from 'molbio/adcs/linker-payload-connection/__generated/LinkerPayloadLeavingGroupsQuery';
import {formatLeavingGroup} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupSerialization';
import {
  getLinkerPayloadLeavingGroupsResult,
  LINKER_PAYLOAD_LEAVING_GROUPS_QUERY,
} from 'molbio/adcs/linker-payload-connection/linkerPayloadLeavingGroupsQuery';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {
  findColumnIndexByGroup,
  getBioentityIdFromEvaluatedCell,
} from 'notebook/tables/search-scoping/rowScopedSearch';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectSearchableDropdown from 'ui-library/dropdowns/SelectSearchableDropdown';

export const SELECT_LINKER_PAYLOAD_FIRST_MESSAGE = 'Select a linker-payload first';
export const LOADING_LEAVING_GROUPS_MESSAGE = 'Loading leaving groups…';
// Matches complex polymer attachments.
export const LEAVING_GROUPS_QUERY_ERROR = 'Error fetching leaving group information.';
// Matches complex polymer attachments. Not an error: the column is optional.
export const NO_LEAVING_GROUP_CONFIGURED_MESSAGE = 'No leaving group configured on the linker-payload.';

const LEAVING_GROUP_DROPDOWN_LABEL = 'Linker-payload leaving group';

const useStyles = createStyles(() => ({
  dropdown: {
    border: 'none',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
}));

interface LeavingGroupPlaceholderProps {
  leavingGroupText: string;
  message: string;
}

/** Shows the selected leaving group, or a hint explaining why no leaving group can be chosen yet. */
function LeavingGroupPlaceholder({leavingGroupText, message}: LeavingGroupPlaceholderProps): JSX.Element {
  if (leavingGroupText !== '') {
    return <Text size="sm">{leavingGroupText}</Text>;
  }
  return (
    <Text size="sm" color="dimmed">
      {message}
    </Text>
  );
}

interface LeavingGroupSelectProps {
  leavingGroups: Array<number>;
  leavingGroupText: string;
  onOpenDropdown?: (() => void) | null;
  onUpdate: (text: string) => void;
}

function LeavingGroupSelect({
  leavingGroups,
  leavingGroupText,
  onOpenDropdown,
  onUpdate,
}: LeavingGroupSelectProps): JSX.Element {
  const {classes} = useStyles();
  const [isOpen, setOpen] = useState(false);

  function handleToggle(newIsOpen: boolean): void {
    if (newIsOpen) {
      onOpenDropdown?.();
    }
    setOpen(newIsOpen);
  }

  // A selected leaving group that is not on the linker-payload still displays; the column's custom eval flags it.
  return (
    <SelectSearchableDropdown
      ariaLabel={LEAVING_GROUP_DROPDOWN_LABEL}
      buttonClassName={classes.dropdown}
      closeOnInsideClick={true}
      isOpen={isOpen}
      menuProps={{isMulti: false}}
      noneSelectedMessage=""
      onSelect={(values) => {
        // The menu reports the current selection followed by the chosen one; choosing the current one again
        // reports neither, which leaves the cell unchanged.
        const [chosen] = values.filter((value) => value !== leavingGroupText);
        if (chosen != null) {
          onUpdate(chosen);
        }
      }}
      onToggle={handleToggle}
      optionDatas={leavingGroups.map((leavingGroup) => {
        const label = formatLeavingGroup(leavingGroup);
        return {name: label, value: label};
      })}
      selectedValues={leavingGroupText === '' ? [] : [leavingGroupText]}
    />
  );
}

interface LeavingGroupDropdownProps {
  moleculeId: string | null;
  leavingGroupText: string;
  onOpenDropdown?: (() => void) | null;
  onUpdate: (text: string) => void;
}

function LeavingGroupDropdown({
  moleculeId,
  leavingGroupText,
  onOpenDropdown,
  onUpdate,
}: LeavingGroupDropdownProps): JSX.Element {
  const queryResult = useSkippableBenchlingQuery<
    LinkerPayloadLeavingGroupsQuery,
    LinkerPayloadLeavingGroupsQueryVariables
  >(LINKER_PAYLOAD_LEAVING_GROUPS_QUERY, {
    variables: {moleculeId: moleculeId ?? ''},
    skip: moleculeId == null,
  });

  if (queryResult.type === QueryResultType.SKIPPED) {
    return (
      <LeavingGroupPlaceholder
        leavingGroupText={leavingGroupText}
        message={SELECT_LINKER_PAYLOAD_FIRST_MESSAGE}
      />
    );
  }
  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <LeavingGroupPlaceholder leavingGroupText={leavingGroupText} message={LOADING_LEAVING_GROUPS_MESSAGE} />
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <LeavingGroupPlaceholder leavingGroupText={leavingGroupText} message={LEAVING_GROUPS_QUERY_ERROR} />
    );
  }
  const leavingGroupsResult = getLinkerPayloadLeavingGroupsResult(queryResult.data);
  if ('error' in leavingGroupsResult) {
    return (
      <LeavingGroupPlaceholder leavingGroupText={leavingGroupText} message={leavingGroupsResult.error} />
    );
  }
  if (leavingGroupsResult.leavingGroups.length === 0) {
    return (
      <LeavingGroupPlaceholder
        leavingGroupText={leavingGroupText}
        message={NO_LEAVING_GROUP_CONFIGURED_MESSAGE}
      />
    );
  }

  return (
    <LeavingGroupSelect
      leavingGroups={leavingGroupsResult.leavingGroups}
      leavingGroupText={leavingGroupText}
      onOpenDropdown={onOpenDropdown}
      onUpdate={onUpdate}
    />
  );
}

interface AdcLinkerPayloadLeavingGroupCellProps {
  /** The cell's text: the selected leaving group label (e.g. "LG1"), or empty. */
  leavingGroupText: string;
  /** Called when the dropdown opens, so the table can select the cell. */
  onOpenDropdown?: (() => void) | null;
  /** Called with the cell's new text when a leaving group is chosen. */
  onUpdate: (text: string) => void;
}

/**
 * Editable Linker-payload leaving group cell in an ADC registration table. Lists the leaving groups on the row's
 * Linker-payload molecule in a dropdown, and shows a hint instead until a linker-payload is chosen. Must render
 * inside a table row (see RowModelProvider).
 */
export default function AdcLinkerPayloadLeavingGroupCell({
  leavingGroupText,
  onOpenDropdown,
  onUpdate,
}: AdcLinkerPayloadLeavingGroupCellProps): JSX.Element {
  const moleculeId = useRowModel(({columnConstraints, evaluatedRow}) =>
    getBioentityIdFromEvaluatedCell(
      evaluatedRow[findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_LINKER_PAYLOAD)]
    )
  );

  return (
    <Box {...getTestProps({component: 'AdcLinkerPayloadLeavingGroupCell', element: 'root'})}>
      <LeavingGroupDropdown
        moleculeId={moleculeId ?? null}
        leavingGroupText={leavingGroupText}
        onOpenDropdown={onOpenDropdown}
        onUpdate={onUpdate}
      />
    </Box>
  );
}
